import React, { useState } from 'react'
import { Gift, AlertCircle, CheckCircle, Loader, X } from 'lucide-react'
import GiftService from '../../services/gifts'

interface GiftRedemptionProps {
  onRedemptionSuccess?: () => void
  isModal?: boolean
  onClose?: () => void
}

const GiftRedemption: React.FC<GiftRedemptionProps> = ({ onRedemptionSuccess, isModal = false, onClose }) => {
  const [giftCode, setGiftCode] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const [isRedeeming, setIsRedeeming] = useState(false)
  const [validationResult, setValidationResult] = useState<{
    valid: boolean
    gift?: any
    error?: string
  } | null>(null)
  const [redemptionResult, setRedemptionResult] = useState<{
    success: boolean
    giftType?: string
    valueAmount?: number
    error?: string
  } | null>(null)

  const handleValidateCode = async () => {
    if (!giftCode.trim()) return

    setIsValidating(true)
    setValidationResult(null)
    
    try {
      const result = await GiftService.validateGiftCode(giftCode.trim().toUpperCase())
      setValidationResult(result)
    } catch (error) {
      setValidationResult({
        valid: false,
        error: 'Failed to validate gift code'
      })
    } finally {
      setIsValidating(false)
    }
  }

  const handleRedeemGift = async () => {
    if (!giftCode.trim()) return

    setIsRedeeming(true)
    setRedemptionResult(null)
    
    try {
      const result = await GiftService.redeemGift(giftCode.trim().toUpperCase())
      setRedemptionResult(result)
      
      if (result.success) {
        setGiftCode('')
        setValidationResult(null)
        onRedemptionSuccess?.()
      }
    } catch (error) {
      setRedemptionResult({
        success: false,
        error: 'Failed to redeem gift'
      })
    } finally {
      setIsRedeeming(false)
    }
  }

  const formatGiftValue = (type: string, amount: number) => {
    if (type === 'credits') {
      return `${amount} image credits`
    } else if (type === 'subscription') {
      return `${amount} month${amount > 1 ? 's' : ''} of premium access`
    }
    return `${amount} value`
  }

  const resetModal = () => {
    setGiftCode('')
    setValidationResult(null)
    setRedemptionResult(null)
    setIsValidating(false)
    setIsRedeeming(false)
  }

  const content = (
    <div className={`bg-white rounded-lg ${isModal ? '' : 'border border-gray-200'} p-6`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Redeem Gift Code</h3>
            <p className="text-sm text-gray-600">Enter a gift code to redeem credits or subscription time</p>
          </div>
        </div>
        {isModal && onClose && (
          <button
            onClick={() => {
              resetModal()
              onClose()
            }}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Gift Code Input */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gift Code
          </label>
          <div className="flex space-x-3">
            <input
              type="text"
              value={giftCode}
              onChange={(e) => {
                setGiftCode(e.target.value.toUpperCase())
                setValidationResult(null)
                setRedemptionResult(null)
              }}
              placeholder="Enter your gift code"
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-center"
              maxLength={12}
            />
            <button
              onClick={handleValidateCode}
              disabled={!giftCode.trim() || isValidating}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 text-gray-700 rounded-lg transition-colors"
            >
              {isValidating ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                'Validate'
              )}
            </button>
          </div>
        </div>

        {/* Validation Result */}
        {validationResult && (
          <div className={`p-4 rounded-lg border ${
            validationResult.valid 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start space-x-3">
              {validationResult.valid ? (
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                {validationResult.valid && validationResult.gift ? (
                  <div>
                    <h4 className="font-semibold text-green-900 mb-2">Valid Gift Found!</h4>
                    <div className="space-y-1 text-sm text-green-800">
                      <p><strong>Gift:</strong> {validationResult.gift.title}</p>
                      <p><strong>Value:</strong> {formatGiftValue(validationResult.gift.type, validationResult.gift.valueAmount)}</p>
                      <p><strong>From:</strong> {validationResult.gift.purchaserName}</p>
                      <p><strong>Expires:</strong> {new Date(validationResult.gift.expiresAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h4 className="font-semibold text-red-900 mb-1">Invalid Gift Code</h4>
                    <p className="text-sm text-red-800">{validationResult.error}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Redemption Result */}
        {redemptionResult && (
          <div className={`p-4 rounded-lg border ${
            redemptionResult.success 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start space-x-3">
              {redemptionResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                {redemptionResult.success ? (
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">Gift Redeemed Successfully! 🎉</h4>
                    <p className="text-sm text-green-800">
                      You've received {formatGiftValue(redemptionResult.giftType || '', redemptionResult.valueAmount || 0)}
                    </p>
                    {isModal && (
                      <button
                        onClick={() => {
                          resetModal()
                          onClose?.()
                        }}
                        className="mt-3 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors text-sm"
                      >
                        Done
                      </button>
                    )}
                  </div>
                ) : (
                  <div>
                    <h4 className="font-semibold text-red-900 mb-1">Redemption Failed</h4>
                    <p className="text-sm text-red-800">{redemptionResult.error}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Redeem Button */}
        {validationResult?.valid && !redemptionResult?.success && (
          <button
            onClick={handleRedeemGift}
            disabled={isRedeeming}
            className="w-full py-3 px-6 bg-purple-500 hover:bg-purple-600 disabled:bg-purple-300 text-white rounded-lg transition-colors flex items-center justify-center"
          >
            {isRedeeming ? (
              <>
                <Loader className="w-5 h-5 animate-spin mr-2" />
                Redeeming Gift...
              </>
            ) : (
              <>
                <Gift className="w-5 h-5 mr-2" />
                Redeem Gift
              </>
            )}
          </button>
        )}
      </div>

      {/* Help Text */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">How to redeem a gift:</h4>
        <ol className="text-sm text-gray-600 space-y-1">
          <li>1. Enter the 12-character gift code you received</li>
          <li>2. Click "Validate" to check if the code is valid</li>
          <li>3. If valid, click "Redeem Gift" to add the benefits to your account</li>
          <li>4. Credits and subscription time will be applied immediately</li>
        </ol>
      </div>
    </div>
  )

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {content}
        </div>
      </div>
    )
  }

  return content
}

export default GiftRedemption 